// frontend/src/hooks/useDataHealthMutations.js
//
// Generic action mutations for the Data Health page.
//
// DataHealth has a parameterised action machine: each issueKey
// (`missing_supplier`, `negative_inventory`, etc.) maps to an endpoint +
// payload shape via `ISSUE_DEFS` in datahealth/registry.js. ONE hook takes the
// issueKey and looks up which kinds of data its fix changes.
//
// The endpoint is passed at mutate-time because one issueKey can resolve
// through different owning ViewSets' bulk-update actions depending on the
// row's target resource, and the save flow (see datahealth/resolveIssue.js)
// chooses bulk-update vs bulk-create per issue. Keeping endpoint as a
// mutate-time arg avoids re-creating the hook per endpoint.

import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { useApiMutation } from '../queries/useApiMutation';


// What a fix of each issue writes (`queries/dataChanged` DATA). One entry per
// issue fixed THROUGH this hook — its def resolves `inline` or `action` in
// datahealth/registry.js. Bespoke, deep-link and external issues write
// elsewhere, and a def with no actionType writes nothing at all, so they have
// none, and useDataHealthAction refuses a write for an issue without one.
// useDataHealthMutations.test.jsx pins the keys against the registry.
export const ISSUE_CHANGES = {
  // A placement is created.
  missing_location:           [DATA.inventory],
  // A default supplier is elected; a PRICED relation also sets the cost.
  missing_supplier:           [DATA.supplierLinks, DATA.items],
  // The cost of record, and with it the default supplier's catalog cost.
  missing_cost:               [DATA.items, DATA.supplierLinks],
  // The count creates the placement's Inventory row.
  missing_inventory:          [DATA.inventory],
  missing_sell_price:         [DATA.items],
  missing_product_category:   [DATA.items],
  missing_sku:                [DATA.items],
  duplicate_sku:              [DATA.items],
  // Tracking puts the item's stock into the stocked universe.
  tracking_off:               [DATA.items, DATA.inventory],
  overselling:                [DATA.items],
  not_purchasable:            [DATA.items],
  digital_item:               [DATA.items],
  negative_inventory:         [DATA.inventory],
  inactive_with_stock:        [DATA.items],
  // The fix writes a cost or a sell price.
  price_below_cost:           [DATA.items, DATA.supplierLinks],
  erp_item_missing_channel_product: [DATA.items],
  // Keep (adopt native) / Retire (soft-delete) an ERP-detached leftover,
  // with its placements.
  erp_leftover_item:          [DATA.items, DATA.inventory],
  // Retire deactivates the location, with its placements.
  erp_leftover_location:      [DATA.locations, DATA.inventory],
  // Deleting the placement cascades to its Inventory rows and its pending
  // recommendation lines.
  stale_placement:            [DATA.inventory, DATA.recommendations],
};


/**
 * Generic data-health row/batch action.
 *
 * Variables: { endpoint, payload, method }
 *   - endpoint: full path, e.g. '/api/items/bulk-update/'
 *   - payload:  bulk-update/create body (`{ items: [{ id, fields }] }`)
 *   - method:   'delete' for a per-row DELETE (no payload); POST otherwise
 *
 * Pre-bound to an issueKey at hook-construction time so the kinds announced
 * are the issue's for every action it surfaces. An issue with no
 * ISSUE_CHANGES entry is refused before any request goes out.
 *
 * Returns the parsed response body (carries `results` array with
 * per-row success/error structure that the page consumes).
 */
export function useDataHealthAction(issueKey) {
  // Every IssueGrid builds this hook, fixable or not, so the lookup is
  // enforced where a write would start rather than here.
  const changes = ISSUE_CHANGES[issueKey];

  return useApiMutation({
    mutationFn: ({ endpoint, payload, method }) => {
      if (!changes) {
        throw new Error(
          `Data Health issue "${issueKey}" has no entry in ISSUE_CHANGES`);
      }
      return method === 'delete' ? apiJson.delete(endpoint) : apiJson.post(endpoint, payload);
    },
    changes: changes ?? [],
  });
}


/**
 * Set the "ignored" flag on a Data Health issue type: an ignored issue
 * leaves the page's checks until included again, and every reader whose
 * price-gap warning honours the mute (backend cost_valuation.disclose_gap)
 * follows.
 *
 * Variables: `{ issue, ignored }` — the state asked for, never a toggle, so
 * a caller's stale view of the current state cannot flip it the wrong way.
 * Resolves once the readers on screen have been read again: the Data Health
 * page moves the switched card to its new section only then.
 */
export function useSetDataHealthIgnored() {
  return useApiMutation({
    mutationFn: (vars) => apiJson.post('/api/data-health/set-ignored/', vars),
    changes: [DATA.dataHealth],
    awaitChanges: true,
  });
}
